import { useCallback, useEffect, useState } from 'react';

const DEFAULT_COOLDOWN_SECONDS = 60;

const storageKey = (email) => `rg_otp_resend_until:${(email || '').toLowerCase()}`;

const readStoredUntil = (email) => {
  if (!email || typeof sessionStorage === 'undefined') return 0;
  const raw = sessionStorage.getItem(storageKey(email));
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const writeStoredUntil = (email, until) => {
  if (!email || typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(storageKey(email), String(until));
};

export default function useResendCooldown(
  email,
  { seconds = DEFAULT_COOLDOWN_SECONDS, startOnMount = true } = {},
) {
  const [cooldownUntil, setCooldownUntil] = useState(() => {
    const stored = readStoredUntil(email);
    if (stored > Date.now()) return stored;
    if (startOnMount) {
      const until = Date.now() + seconds * 1000;
      writeStoredUntil(email, until);
      return until;
    }
    return 0;
  });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const stored = readStoredUntil(email);
    if (stored > Date.now()) {
      setCooldownUntil(stored);
    } else if (startOnMount) {
      const until = Date.now() + seconds * 1000;
      writeStoredUntil(email, until);
      setCooldownUntil(until);
    }
  }, [email, seconds, startOnMount]);

  const secondsLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const isCoolingDown = secondsLeft > 0;

  const startCooldown = useCallback(
    (overrideSeconds = seconds) => {
      const until = Date.now() + overrideSeconds * 1000;
      writeStoredUntil(email, until);
      setCooldownUntil(until);
      setNow(Date.now());
    },
    [email, seconds],
  );

  const formatTime = useCallback(() => {
    const m = Math.floor(secondsLeft / 60);
    const s = secondsLeft % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }, [secondsLeft]);

  return {
    secondsLeft,
    isCoolingDown,
    canResend: !isCoolingDown,
    startCooldown,
    formatTime,
  };
}
